"use client";

import { useState } from "react";
import axios from "axios";
import { useRouter } from "next/navigation";
import { toast } from "react-hot-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Input } from "../../components/ui/input";
import { ButtonS } from "../../components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form";
import Container from "../components/Container";
import Heading from "../components/Heading";
import { FaCheck, FaClock, FaDollarSign, FaEye, FaEyeSlash, FaRocket } from "react-icons/fa";
import { AffiliateSchema } from "../schemas";
import { SafeUser } from "../types";
import { signIn } from "next-auth/react";

interface AffiliatesSelectProps {
  currentUser?: SafeUser | null;
}

const AffiliatesPage: React.FC<AffiliatesSelectProps> = ({
  currentUser,
}) => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>("");
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<z.infer<typeof AffiliateSchema>>({
    resolver: zodResolver(AffiliateSchema),
    defaultValues: {
      email: "",
      name: "",
      password: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof AffiliateSchema>) => {
    setIsLoading(true);
    setError("");

    try {
      const response = await axios.post("/api/affiliates/register", values);

      const { user, referralCode } = response.data;
      const userId = user.id;

      const signInResponse = await signIn("credentials", {
        redirect: false,
        email: values.email,
        password: values.password,
      });

      if (!signInResponse?.ok) {
        throw new Error("Failed to sign in automatically");
      }

      toast.success(`Registered as an affiliate! Your referral code: ${referralCode}`);

      // Redirección al dashboard del afiliado
      setTimeout(() => {
        router.push(`/affiliates-dashboard/${userId}`);
      }, 1000); // Pequeño delay para que se complete el signIn

    } catch (error: any) {
      const errorMessage = error.response?.data?.error || "Failed to register as an affiliate.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const benefits = [
    {
      icon: <FaDollarSign className="text-green-400" size={24} />,
      title: "40% Commission",
      description: "Earn 40% on every sale you generate"
    },
    {
      icon: <FaClock className="text-blue-400" size={24} />,
      title: "Payment in 48 Hours",
      description: "Receive your money via Stripe in just 48 hours"
    },
    {
      icon: <FaRocket className="text-purple-400" size={24} />,
      title: "Automatic System",
      description: "Everything works automatically, you just promote"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
      <Container>
        <div className="py-12">
          {/* Header Section */}
          <div className="text-center mb-12">
            <div className="inline-block bg-gradient-to-r from-cyan-500 to-blue-500 text-black px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Affiliate Program Active!
            </div>
            <h1 className="text-3xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Join Peerquik Affiliate Program
            </h1>
            <p className="text-lg text-gray-300 mb-8">
              Turn your audience into steady income
            </p>

            {/* Benefits Cards */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-gray-800/50 backdrop-blur border border-gray-700 rounded-2xl p-6 hover:bg-gray-800/80 transition-all duration-300">
                  <div className="flex justify-center mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p className="text-gray-400">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>


          <div className="max-w-3xl mx-auto bg-gray-800/50 backdrop-blur border border-gray-700 rounded-2xl p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold mb-2">Get Started Now</h2>
              <p className="text-gray-400">Sign up for free in less than 2 minutes</p>
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-300 font-medium">Email</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="your@email.com"
                            type="email"
                            className="bg-gray-900/50 border-gray-600 text-white placeholder-gray-500 h-12 rounded-xl focus:border-cyan-500 focus:ring-cyan-500/20"
                          />
                        </FormControl>
                        <FormMessage className="text-red-400 text-xs" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-300 font-medium">
                          Name
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Your name"
                            className="bg-gray-900/50 border-gray-600 text-white placeholder-gray-500 h-12 rounded-xl focus:border-cyan-500 focus:ring-cyan-500/20"
                          />
                        </FormControl>
                        <FormMessage className="text-red-400 text-xs" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-300 font-medium">Password</FormLabel>
                        <div className="relative">
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Minimum 6 characters"
                              type={showPassword ? "text" : "password"}
                              className="bg-gray-900/50 border-gray-600 text-white placeholder-gray-500 h-12 rounded-xl focus:border-cyan-500 focus:ring-cyan-500/20 pr-12"
                            />
                          </FormControl>
                          <button
                            type="button"
                            onClick={() => setShowPassword((prev) => !prev)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                          >
                            {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                          </button>
                        </div>
                        <FormMessage className="text-red-400 text-xs" />
                      </FormItem>
                    )}
                  />
                </div>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3">
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}

                <ButtonS
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-black font-bold text-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02]"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                      Processing...
                    </div>
                  ) : (
                    "🚀 Start Earning Money"
                  )}
                </ButtonS>

                <p className="text-xs text-gray-500 text-center">
                  By signing up, you agree to our terms and conditions
                </p>
              </form>
            </Form>

            <div className="text-center mt-6 pt-6 border-t border-gray-700">
              <p className="text-gray-400 text-sm">
                Already have an account?{" "}
                <button
                  onClick={() => router.push("/login")}
                  className="text-cyan-400 hover:text-cyan-300 font-medium hover:underline transition"
                >
                  Log In
                </button>
              </p>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default AffiliatesPage;